/**
 * Legacy localStorage migration
 *
 * The old system stored data under separate keys:
 *   smooth_builder_components
 *   smooth_builder_order
 *   smooth_builder_settings
 *   smooth_builder_builderTheme
 *
 * The new Zustand persist middleware uses a single key: "smooth_builder"
 *
 * This module provides a one-time migration from the old format to the new one.
 */

const LEGACY_PREFIX = 'smooth_builder_';

interface LegacyData {
  components?: any;
  order?: string[];
  settings?: any;
  builderTheme?: 'dark' | 'light';
}

function loadLegacy(key: string): any {
  try {
    const raw = localStorage.getItem(LEGACY_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Attempts to read legacy localStorage keys.
 * Returns null if no legacy data is found.
 */
export function migrateFromLegacyStorage(): LegacyData | null {
  const components = loadLegacy('components');
  const order = loadLegacy('order');
  const settings = loadLegacy('settings');
  const builderTheme = loadLegacy('builderTheme');

  if (!components && !order && !settings && !builderTheme) {
    return null;
  }

  return {
    ...(components ? { components } : {}),
    ...(order ? { order } : {}),
    ...(settings ? { settings } : {}),
    ...(builderTheme ? { builderTheme } : {}),
  };
}

/**
 * Removes legacy localStorage keys after successful migration.
 */
export function removeLegacyStorage(): void {
  const keys = ['components', 'order', 'settings', 'builderTheme'];
  keys.forEach((key) => {
    try {
      localStorage.removeItem(LEGACY_PREFIX + key);
    } catch {
      // ignore
    }
  });
}
